/**
 * DashboardWriteService.js
 *
 * Service for writing dashboard data to Firestore.
 */

// Firebase
import { collection, deleteDoc, doc, getDocs, query, setDoc, Timestamp, updateDoc, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Utilities
import { generateKey } from 'utilities/Keys';

// Constants
const DASHBOARDS_COLLECTION = collections.dashboards;
const DASHBOARD_SCOPE = Object.freeze({
  GLOBAL: 'GLOBAL',
  NODE_TYPE: 'NODE_TYPE',
});

function normalizeScope(scope) {
  const candidate = typeof scope === 'string' ? scope.trim().toUpperCase() : '';
  if (candidate === DASHBOARD_SCOPE.NODE_TYPE) return DASHBOARD_SCOPE.NODE_TYPE;
  return DASHBOARD_SCOPE.GLOBAL;
}

function toNodeTypeDashboardKey(typeKey) {
  const trimmed = typeof typeKey === 'string' ? typeKey.trim() : '';
  if (!trimmed) return '';
  return `nodetype_${trimmed.replace(/\//g, '_')}_home`;
}

function normalizeCards(cards) {
  if (!Array.isArray(cards)) return [];

  const seenViewIds = new Set();
  const sanitized = [];

  cards.forEach((card, index) => {
    const viewId = typeof card?.viewId === 'string' ? card.viewId.trim() : '';
    if (!viewId || seenViewIds.has(viewId)) return;

    seenViewIds.add(viewId);

    sanitized.push({
      key: typeof card?.key === 'string' && card.key.trim() ? card.key.trim() : generateKey(),
      viewId,
      columnIndex: Number.isFinite(Number(card?.columnIndex)) ? Math.max(0, Number(card.columnIndex)) : 0,
      position: Number.isFinite(Number(card?.position)) ? Math.max(0, Number(card.position)) : index,
      titleOverride: typeof card?.titleOverride === 'string' && card.titleOverride.trim()
        ? card.titleOverride.trim()
        : null,
      createdAt: card?.createdAt || Timestamp.now(),
      updatedAt: card?.updatedAt || Timestamp.now(),
    });
  });

  return sanitized;
}

function normalizeDescription(description) {
  const trimmed = typeof description === 'string' ? description.trim() : '';
  return trimmed || null;
}

/**
 * Creates a new dashboard document.
 *
 * @param {Object} options - Create options.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {string} options.userKey - Author user key.
 * @param {string} options.title - Dashboard title.
 * @param {string} [options.description] - Optional dashboard description.
 * @param {number} [options.columnCount=3] - Initial column count.
 * @param {boolean} [options.isPrivate=false] - Whether dashboard is private.
 *
 * @returns {Promise<Object|null>} Created dashboard payload.
 */
export async function createDashboard({
  key,
  workspaceKey,
  userKey,
  title,
  description,
  columnCount = 3,
  isPrivate: rawIsPrivate = false,
  scope: rawScope,
  anchorTypeKey: rawAnchorTypeKey = null,
} = {}) {

  const trimmedTitle = typeof title === 'string' ? title.trim() : '';
  const normalizedDescription = normalizeDescription(description);
  const normalizedColumns = Number(columnCount) === 2 ? 2 : 3;
  const isPrivate = rawIsPrivate === true;
  const scope = normalizeScope(rawScope);
  const anchorTypeKey = typeof rawAnchorTypeKey === 'string' && rawAnchorTypeKey.trim()
    ? rawAnchorTypeKey.trim()
    : null;

  if (!workspaceKey || !userKey || !trimmedTitle) return null;
  if (scope === DASHBOARD_SCOPE.NODE_TYPE && !anchorTypeKey) return null;

  const dashboardKey = typeof key === 'string' && key.trim() ? key.trim() : generateKey();
  const now = Timestamp.now();

  const payload = {
    key: dashboardKey,
    workspaceKey,
    scope,
    anchorTypeKey: scope === DASHBOARD_SCOPE.NODE_TYPE ? anchorTypeKey : null,
    title: trimmedTitle,
    titleLower: trimmedTitle.toLowerCase(),
    description: normalizedDescription,
    columnCount: normalizedColumns,
    private: isPrivate,
    cards: [],
    createdBy: userKey,
    createdAt: now,
    updatedAt: now,
    visibility: null,
  };

  await setDoc(doc(db, DASHBOARDS_COLLECTION, dashboardKey), payload, { merge: false });

  return payload;
}

/**
 * Ensures a node-type-scoped Home dashboard exists and returns it.
 *
 * @param {Object} options - Request options.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {string} options.typeKey - Anchor node type key.
 * @param {string} options.userKey - Author user key.
 * @param {string} [options.title='Home'] - Dashboard title.
 * @param {number} [options.columnCount=2] - Column count.
 *
 * @returns {Promise<Object|null>} Existing or created dashboard.
 */
export async function getOrCreateNodeTypeDashboard({ workspaceKey, typeKey, userKey, title = 'Home', columnCount = 3 } = {}) {

  const cleanedWorkspaceKey = typeof workspaceKey === 'string' ? workspaceKey.trim() : '';
  const cleanedTypeKey = typeof typeKey === 'string' ? typeKey.trim() : '';
  const cleanedUserKey = typeof userKey === 'string' ? userKey.trim() : '';
  const cleanedTitle = typeof title === 'string' && title.trim() ? title.trim() : 'Home';
  const normalizedColumns = Number(columnCount) === 2 ? 2 : 3;

  if (!cleanedWorkspaceKey || !cleanedTypeKey || !cleanedUserKey) return null;

  const dashboardKey = toNodeTypeDashboardKey(cleanedTypeKey);
  if (!dashboardKey) return null;

  const now = Timestamp.now();
  const payload = {
    key: dashboardKey,
    workspaceKey: cleanedWorkspaceKey,
    scope: DASHBOARD_SCOPE.NODE_TYPE,
    anchorTypeKey: cleanedTypeKey,
    title: cleanedTitle,
    titleLower: cleanedTitle.toLowerCase(),
    columnCount: normalizedColumns,
    createdBy: cleanedUserKey,
    updatedAt: now,
    visibility: null,
  };

  // Merge avoids clobbering existing cards while still creating missing docs.
  await setDoc(doc(db, DASHBOARDS_COLLECTION, dashboardKey), payload, { merge: true });

  return { ...payload, id: dashboardKey };
}

/**
 * Updates an existing dashboard document.
 *
 * @param {string} dashboardKey - Dashboard key.
 * @param {Object} data - Fields to update.
 *
 * @returns {Promise<void>}
 */
export async function updateDashboard(dashboardKey, data = {}) {

  if (!dashboardKey || !data) return;

  const payload = {
    ...data,
    ...(Object.prototype.hasOwnProperty.call(data, 'description') ? { description: normalizeDescription(data.description) } : {}),
    ...(Object.prototype.hasOwnProperty.call(data, 'cards') ? { cards: normalizeCards(data.cards) } : {}),
    updatedAt: data?.updatedAt || Timestamp.now(),
  };

  await updateDoc(doc(db, DASHBOARDS_COLLECTION, dashboardKey), payload);
}

/**
 * Deletes a dashboard by key.
 *
 * @param {string} dashboardKey - Dashboard key.
 *
 * @returns {Promise<void>}
 */
export async function deleteDashboardByKey(dashboardKey) {

  if (!dashboardKey) return;

  await deleteDoc(doc(db, DASHBOARDS_COLLECTION, dashboardKey));
}

/**
 * Removes a deleted view reference from dashboard cards.
 *
 * This function is idempotent: running it multiple times is safe.
 *
 * @param {Object} options - Cleanup options.
 * @param {string} options.viewId - Deleted view key.
 * @param {string} [options.workspaceKey] - Optional workspace key for narrower query.
 *
 * @returns {Promise<number>} Number of dashboards updated.
 */
export async function removeViewFromDashboards({ viewId, workspaceKey } = {}) {

  const cleanedViewId = typeof viewId === 'string' ? viewId.trim() : '';
  const cleanedWorkspaceKey = typeof workspaceKey === 'string' ? workspaceKey.trim() : '';

  if (!cleanedViewId) return 0;

  const dashboardsQuery = cleanedWorkspaceKey
    ? query(collection(db, DASHBOARDS_COLLECTION), where('workspaceKey', '==', cleanedWorkspaceKey))
    : query(collection(db, DASHBOARDS_COLLECTION));

  const snapshot = await getDocs(dashboardsQuery);
  if (!snapshot?.docs?.length) return 0;

  const updates = [];

  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const cards = Array.isArray(data?.cards) ? data.cards : [];
    if (!cards.length) return;

    const filtered = cards.filter((card) => String(card?.viewId || '').trim() !== cleanedViewId);
    if (filtered.length === cards.length) return;

    const normalized = normalizeCards(filtered);
    updates.push(
      updateDoc(doc(db, DASHBOARDS_COLLECTION, docSnap.id), {
        cards: normalized,
        updatedAt: Timestamp.now(),
      })
    );
  });

  if (!updates.length) return 0;

  await Promise.all(updates);
  return updates.length;
}
